/**
 * PlannerContext - Central state management for meal planners and shopping lists
 *
 * ## Architecture Overview
 *
 * This context manages two related features:
 * 1. Meal Planners - Multi-day recipe planning
 * 2. Shopping Lists - Recipe collection for shopping (implemented using planner infrastructure)
 *
 * ## Shopping List Implementation
 *
 * Shopping lists leverage the existing planner system for rapid development:
 *
 * - A "shopping list" is implemented as a special Planner with a hardcoded title (not a shared GUID)
 * - The magic title constant is defined as SHOPPING_LIST_GUID in ShoppingListContainer.tsx
 * - Each user gets their own shopping list planner with this title
 * - This planner is marked as `isHidden: true` so it doesn't appear in regular planner lists
 * - "Adding a recipe to shopping list" means adding it to days[0].dayRecipes of this special planner
 * - The shopping list only uses the first day (days[0]) of this planner
 * - Each recipe maintains its full structure with cachedData.recipeIngredients for shopping
 *
 * ## Data Flow for Shopping Lists:
 * ```typescript
 * // Find the shopping list (it's a user-specific planner with a magic title)
 * const shoppingList = planners.find(planner => planner.title === SHOPPING_LIST_GUID);
 *
 * // Get recipes from the first day of this planner
 * const recipes = shoppingList?.days?.[0]?.dayRecipes;
 *
 * // Access ingredients for shopping
 * recipes.map(({ recipe }) => recipe.cachedData.recipeIngredients)
 * ```
 *
 * ## Important Notes for Developers:
 * - Shopping lists and planners share the same data structures and API endpoints
 * - When working with shopping lists, you're actually working with a single-day planner
 * - The terminology can be confusing since "planner" concepts are used for shopping functionality
 * - Always check for null/undefined when accessing planner properties
 *

 */

// PlannerContext.ts
import { SHOPPING_LIST_GUID } from "@/components/ShoppingList/ShoppingListContainer";
import fetchCopyPlanner from "@/lib/planner/fetchCopyPlanner";
import fetchDeletePlanner from "@/lib/planner/fetchDeletePlanner";
import fetchUpdatePlannerDays from "@/lib/planner/fetchUpdatePlannerDays";
import {
  PlannerDayWithRecipes,
  PlannerWithDaysAndRecipes,
} from "@/types/planner";
import { useFeatureFlagVariantKey } from "posthog-js/react";
import React, { createContext, useContext, useEffect, useState } from "react";

interface PlannerContextValue {
  planners: Array<PlannerWithDaysAndRecipes>;
  sortedPlanners: Array<PlannerWithDaysAndRecipes>;
  findPlannerByUid: (uid: string) => PlannerWithDaysAndRecipes | undefined;
  lastFetchedAt: Date | null;
  setPlanners: (planners: Array<PlannerWithDaysAndRecipes>) => void;
  setLastFetchedAt: (lastFetchedAt: Date) => void;
  isRecipeInPlanners: (recipeId: number) => boolean;
  updatePlanner: (
    uid: string,
    newPlanner: PlannerWithDaysAndRecipes,
    markAsSaved?: boolean
  ) => void;
  addPlanner: (planner: PlannerWithDaysAndRecipes) => void;
  isShoppingListOpen: boolean;
  setIsShoppingListOpen: (isShoppingListOpen: boolean) => void;
  markPlannerAsUnsaved: (uid: string) => void;
  markPlannerAsSaved: (uid: string) => void;
  isPlannerUnsaved: (uid: string) => boolean;
  isPlannerSaving: (uid: string) => boolean;
  savePlanner: (
    uid: string,
    overridePlanner: PlannerWithDaysAndRecipes
  ) => Promise<void>;
  isPlannerDeleting: (uid: string) => boolean;
  deletePlanner: (uid: string) => Promise<void>;
  autosaveEnabled: boolean;
  setAutosaveEnabled: (autosaveEnabled: boolean) => void;
  plannerCount: number;
  sortingOptions: { value: string; label: string }[];
  selectedSortingOption: string | null;
  setSelectedSortingOption: (value: string | null) => void;
  sortDirection: "asc" | "desc";
  setSortDirection: (direction: "asc" | "desc") => void;
  copyPlanner: (
    uid: string,
    newUserId: string
  ) => Promise<PlannerWithDaysAndRecipes | undefined>;
  // Shopping list utilities
  getShoppingList: () => PlannerWithDaysAndRecipes | undefined;
  getShoppingListRecipeCount: () => number;
  canAddToShoppingList: (userPlan: number) => boolean;
  // Experiment utilities
  shouldAllowFreeUsersShoppingList: boolean;
  canUserAccessShoppingList: (userPlan: number) => boolean;
}
const PlannerContext = createContext<PlannerContextValue | null>(null);

const usePlannerContext = () => {
  const context = useContext(PlannerContext);
  if (!context) {
    throw new Error(
      "usePlannerContext must be used within a PlannerContextProvider"
    );
  }
  return context;
};

const sortingOptions = [
  { value: "date", label: "Date" },
  { value: "alpha", label: "Alphabetical" },
];

const PlannerContextProvider: React.FC = ({ children }) => {
  const [planners, setPlanners] = useState<Array<PlannerWithDaysAndRecipes>>(
    []
  );
  const [sortedPlanners, setSortedPlanners] = useState<
    Array<PlannerWithDaysAndRecipes>
  >([]);
  const [autosaveEnabled, setAutosaveEnabled] = useState<boolean>(false);
  const [unsavedPlanners, setUnsavedPlanners] = useState<string[]>([]);
  const [savingPlanners, setSavingPlanners] = useState<string[]>([]);
  const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null);

  const [plannerCount, setPlannerCount] = useState<number>(0);

  const [selectedSortingOption, setSelectedSortingOption] = useState<
    string | null
  >(sortingOptions.length > 0 ? sortingOptions[0].value : null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  // PostHog experiment for shopping list access
  const experimentVariant = useFeatureFlagVariantKey(
    "cro-web-147-shopping-list-to-free-users-paywall"
  );

  // DEBUG: Log the experiment variant in PlannerContext (not in production)
  if (process.env.NEXT_PUBLIC_VERCEL_ENV !== "production") {
    console.log("🔬 PlannerContext experiment variant:", experimentVariant);
  }

  // Determine if free users should have access based on experiment
  // Default to control (blocking free users) unless explicitly in a treatment variant
  const shouldAllowFreeUsersShoppingList =
    experimentVariant === "with-recipe-hero-trial-cta" ||
    experimentVariant === "without-recipe-hero-trial-cta";

  // DEBUG: Log the computed access state (not in production)
  if (process.env.NEXT_PUBLIC_VERCEL_ENV !== "production") {
    console.log(
      "🔬 shouldAllowFreeUsersShoppingList:",
      shouldAllowFreeUsersShoppingList
    );
  }

  // Determine if a user can access shopping lists based on their plan and experiment
  const canUserAccessShoppingList = (userPlan: number): boolean => {
    if (shouldAllowFreeUsersShoppingList) {
      // Non-control variants: Allow both premium (plan 2) and free (plan 3) users
      return userPlan === 2 || userPlan === 3;
    } else {
      // Control: Only premium users (plan 2)
      return userPlan === 2;
    }
  };

  // Update plannerCount whenever planners change
  useEffect(() => {
    setPlannerCount(planners.length);
  }, [planners]);

  // Sync plannerCount with localStorage whenever it changes
  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("plannerCount", JSON.stringify(plannerCount));
    }
  }, [plannerCount]);

  // Read from localStorage when component mounts
  useEffect(() => {
    if (typeof window !== "undefined") {
      const storedPlannerCount = localStorage.getItem("plannerCount");
      if (storedPlannerCount) {
        setPlannerCount(JSON.parse(storedPlannerCount));
      }
    }
  }, []);

  // Sorting function
  const sortPlanners = (plannersToSort: Array<PlannerWithDaysAndRecipes>) => {
    return [...plannersToSort].sort((a, b) => {
      if (selectedSortingOption === "alpha") {
        // Sorting by title (alphabetically)
        const titleA = a.title.toLowerCase();
        const titleB = b.title.toLowerCase();
        if (titleA < titleB) return sortDirection === "asc" ? -1 : 1;
        if (titleA > titleB) return sortDirection === "asc" ? 1 : -1;
        return 0;
      } else {
        // Sorting by date (using createdAt)
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();
        return sortDirection === "asc" ? dateA - dateB : dateB - dateA;
      }
    });
  };

  // Watch for changes in sorting options or planners and re-sort planners
  useEffect(() => {
    const sorted = sortPlanners(planners);
    setSortedPlanners(sorted);
  }, [selectedSortingOption, sortDirection, planners]);

  const findPlannerByUid = (uid: string) => {
    return planners.find((planner) => planner.uid === uid);
  };

  const isRecipeInPlanners = (recipeId: number): boolean => {
    return planners.some((planner) =>
      planner.days.some((day) =>
        day.dayRecipes.some((dayRecipe) => dayRecipe.recipeId === recipeId)
      )
    );
  };

  const markPlannerAsUnsaved = (uid: string) => {
    setUnsavedPlanners((prevUnsavedPlanners) =>
      prevUnsavedPlanners.includes(uid)
        ? prevUnsavedPlanners
        : [...prevUnsavedPlanners, uid]
    );
  };

  const markPlannerAsSaved = (uid: string) => {
    setUnsavedPlanners((prevUnsavedPlanners) =>
      prevUnsavedPlanners.filter((plannerId) => plannerId !== uid)
    );
  };

  const isPlannerUnsaved = (uid: string) => {
    return unsavedPlanners.includes(uid);
  };

  const isPlannerSaving = (uid: string): boolean => {
    return savingPlanners.includes(uid);
  };

  const [deletingPlanners, setDeletingPlanners] = useState<string[]>([]);

  const isPlannerDeleting = (uid: string): boolean => {
    return deletingPlanners.includes(uid);
  };

  const deletePlanner = async (uid: string) => {
    const planner = findPlannerByUid(uid);

    if (!planner) {
      console.error("Could not find planner with given uid");
      return;
    }

    setDeletingPlanners((prev) => [...prev, uid]); // Mark planner as being deleted

    try {
      // Call API to delete planner
      await fetchDeletePlanner(uid);

      // If the above line doesn't throw an error, the deletion was successful
      setPlanners((prev) => prev.filter((p) => p.uid !== uid)); // remove deleted planner from planners

      // Mark planner as done deleting
      setDeletingPlanners((prev) =>
        prev.filter((plannerId) => plannerId !== uid)
      );
    } catch (error) {
      // Handle failure case here
      setDeletingPlanners((prev) =>
        prev.filter((plannerId) => plannerId !== uid)
      );
      console.error("Error deleting planner:", error);
    }
  };

  const updatePlanner = (
    uid: string,
    newPlanner: PlannerWithDaysAndRecipes,
    markAsSaved = false
  ): void => {
    setPlanners((prevPlanners) =>
      prevPlanners.map((planner) =>
        planner.uid === uid ? newPlanner : planner
      )
    );
    if (!markAsSaved) markPlannerAsUnsaved(uid);
    else markPlannerAsSaved(uid);
  };

  const addPlanner = (planner: PlannerWithDaysAndRecipes) => {
    setPlanners((prevPlanners) => [...prevPlanners, planner]);
  };

  const [isShoppingListOpen, setIsShoppingListOpen] = useState(false);

  const savePlanner = async (
    uid: string,
    overridePlanner: PlannerWithDaysAndRecipes
  ) => {
    const planner = overridePlanner ?? findPlannerByUid(uid);

    if (!planner) {
      console.error("Could not find planner with given uid");
      return;
    }

    setSavingPlanners((prev) => [...prev, uid]); // Mark planner as being saved

    // planner.days should now correctly match the type expected by fetchUpdatePlannerDays
    // because PlannerWithDaysAndRecipes (from @/types/planner) defines days correctly.
    const updatedDays = await fetchUpdatePlannerDays(planner.uid, planner.days);

    if (updatedDays) {
      const updatedPlannerData = {
        ...planner,
        days: updatedDays as PlannerDayWithRecipes[],
      };
      updatePlanner(
        planner.uid,
        updatedPlannerData as PlannerWithDaysAndRecipes,
        true
      );
    } else {
      console.error("Error updating planner");
    }

    setSavingPlanners((prev) => prev.filter((pUid) => pUid !== planner.uid)); // Mark planner as done saving
  };

  const copyPlanner = async (
    uid: string,
    newUserId: string
  ): Promise<PlannerWithDaysAndRecipes | undefined> => {
    const planner = findPlannerByUid(uid);

    if (!planner) {
      console.error("Could not find planner with given uid");
      return undefined;
    }

    try {
      const response = await fetchCopyPlanner(uid, newUserId);
      console.log("copiedPlanner response", response);

      if (response.success && response.copiedPlanner) {
        // Ensure response.copiedPlanner conforms to PlannerWithDaysAndRecipes
        // It might be necessary to cast or transform it if its type from API is different.
        const newPlanner = response.copiedPlanner as PlannerWithDaysAndRecipes;
        setPlanners((prev) => [...prev, newPlanner]);
        return newPlanner;
      } else {
        console.error(
          "Error copying planner: Response indicates failure or missing planner data"
        );
        return undefined;
      }
    } catch (error) {
      console.error("Error copying planner:", error);
      return undefined;
    }
  };

  // Shopping list utilities
  const getShoppingList = (): PlannerWithDaysAndRecipes | undefined => {
    return planners.find((planner) => planner.title === SHOPPING_LIST_GUID);
  };

  const getShoppingListRecipeCount = (): number => {
    const shoppingList = getShoppingList();
    return shoppingList?.days?.[0]?.dayRecipes?.length || 0;
  };

  const canAddToShoppingList = (userPlan: number): boolean => {
    const currentRecipeCount = getShoppingListRecipeCount();

    // Premium users (plan 2) can add unlimited recipes
    if (userPlan === 2) {
      return true;
    }

    // Free users can only add up to 2 recipes
    return currentRecipeCount < 2;
  };

  const value: PlannerContextValue = {
    // Core planner data and utilities
    planners,
    sortedPlanners,
    findPlannerByUid,
    lastFetchedAt,
    setPlanners,
    setLastFetchedAt,
    isRecipeInPlanners,
    plannerCount,

    // Planner CRUD operations
    updatePlanner,
    addPlanner,
    savePlanner,
    deletePlanner,
    copyPlanner,

    // Planner state management
    markPlannerAsSaved,
    markPlannerAsUnsaved,
    isPlannerUnsaved,
    isPlannerSaving,
    isPlannerDeleting,
    autosaveEnabled,
    setAutosaveEnabled,

    // Shopping list state (implemented as special planner)
    isShoppingListOpen,
    setIsShoppingListOpen,

    // Sorting and display options
    sortingOptions,
    selectedSortingOption,
    setSelectedSortingOption,
    sortDirection,
    setSortDirection,

    // Shopping list utilities
    getShoppingList,
    getShoppingListRecipeCount,
    canAddToShoppingList,

    // Experiment utilities
    shouldAllowFreeUsersShoppingList,
    canUserAccessShoppingList,
  };

  return (
    <PlannerContext.Provider value={value}>{children}</PlannerContext.Provider>
  );
};

export { PlannerContext, PlannerContextProvider, usePlannerContext };
